/*  WebinarManager — schedule Live Sessions for the doctor Academy.
    ────────────────────────────────────────────────────────────────
    Used by Certification Authors AND Admins (same component, wired
    into `/creator/webinars` and `/admin/webinars`). Backend is the
    single `/api/staff/webinars` router which accepts any staff-with-
    course-creator OR admin/super caller.

    UX: list + inline add/edit dialog + delete confirm. External URL
    only (Zoom/Meet/Teams paste-in). Featured toggle pins the session
    to the top of the doctor's Live Sessions tab.
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    Broadcast, Plus, PencilSimple, Trash, CalendarBlank, Users,
    Sparkle, VideoCamera, LinkSimple, Clock,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { confirmDialog } from "@/lib/confirm";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";


/* Canonical StatTile — matches Work/Content sections exactly. */
function StatTile({ label, value, Icon, tone = "primary", onClick, active, testid }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border p-3 text-left transition ${
                active ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                       : (onClick ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer" : "border-border")
            }`}
            aria-pressed={onClick ? active : undefined}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
            </div>
        </Component>
    );
}


const AUDIENCE_OPTIONS = [
    { value: "all", label: "All doctors" },
    { value: "orthodontist", label: "Orthodontists" },
    { value: "general_dentist", label: "General dentists" },
];


function isPast(iso, durationMin) {
    if (!iso) return false;
    const end = new Date(iso).getTime() + (Number(durationMin || 0) * 60_000);
    return Date.now() > end;
}


export default function WebinarManager() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    const overline = isAdmin ? "Content · Live Sessions" : "Author · Live Sessions";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | webinar object
    const [q, setQ] = useState("");
    const [chip, setChip] = useState("all"); // all | upcoming | featured | past

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/staff/webinars");
            setItems(data.items || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load sessions.");
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const stats = useMemo(() => {
        const s = { total: items.length, upcoming: 0, featured: 0, past: 0 };
        items.forEach((w) => {
            const p = isPast(w.start_at, w.duration_minutes);
            if (p) s.past += 1; else s.upcoming += 1;
            if (w.featured) s.featured += 1;
        });
        return s;
    }, [items]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return items.filter((w) => {
            const past = isPast(w.start_at, w.duration_minutes);
            if (chip === "upcoming" && past) return false;
            if (chip === "past" && !past) return false;
            if (chip === "featured" && !w.featured) return false;
            if (!term) return true;
            return (w.title || "").toLowerCase().includes(term)
                || (w.description || "").toLowerCase().includes(term)
                || (w.host_name || "").toLowerCase().includes(term);
        });
    }, [items, q, chip]);

    const remove = useCallback(async (row) => {
        const ok = await confirmDialog({
            title: "Delete this session?",
            message: `"${row.title}" will disappear from the Academy immediately. RSVPs will be cleared.`,
            confirmText: "Delete",
            tone: "warn",
            testid: "webinar-delete-confirm",
        });
        if (!ok) return;
        try {
            await api.delete(`/staff/webinars/${row.id}`);
            toast.success("Session deleted.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete.");
        }
    }, [load]);

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8 space-y-6" data-testid="webinar-manager">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">{overline}</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">
                        Webinars &amp; Live Q&amp;As
                    </h1>
                    <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                        Schedule live webinars, clinical Q&amp;As, and practice-growth sessions for the Invisique doctor community. Paste a Zoom / Meet / Teams link; RSVPs and reminders happen inside Invisique.
                    </p>
                </div>
                <Button size="sm" className="rounded-full" onClick={() => setEditing("new")} data-testid="webinar-new-btn">
                    <Plus size={14} weight="bold" className="mr-1.5" />
                    Schedule Session
                </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="webinar-kpi-strip">
                <StatTile label="Total sessions" value={stats.total}    Icon={VideoCamera} tone="primary" onClick={() => setChip("all")}       active={chip === "all"}      testid="webinar-kpi-total" />
                <StatTile label="Upcoming"       value={stats.upcoming} Icon={CalendarBlank} tone="emerald" onClick={() => setChip("upcoming")} active={chip === "upcoming"} testid="webinar-kpi-upcoming" />
                <StatTile label="Featured"       value={stats.featured} Icon={Sparkle}     tone="amber"    onClick={() => setChip("featured")} active={chip === "featured"} testid="webinar-kpi-featured" />
                <StatTile label="Past"           value={stats.past}     Icon={Clock}       tone="slate"    onClick={() => setChip("past")}     active={chip === "past"}     testid="webinar-kpi-past" />
            </div>

            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search title, description, host…"
                searchTestid="webinar-search"
                testid="webinar-search-bar"
            />

            <FilterChipRow
                testid="webinar-chip-row"
                chipTestidPrefix="webinar-chip"
                value={chip}
                onChange={setChip}
                chips={[
                    { id: "all",      label: "All",      count: stats.total,    tone: "primary" },
                    { id: "upcoming", label: "Upcoming", count: stats.upcoming, tone: "emerald" },
                    { id: "featured", label: "Featured", count: stats.featured, tone: "amber" },
                    { id: "past",     label: "Past",     count: stats.past,     tone: "slate" },
                ]}
            />

            <div>
                {loading ? (
                    <div className="space-y-3">
                        {[0, 1, 2].map((i) => <div key={i} className="h-28 bg-secondary/50 rounded-2xl animate-pulse" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="webinar-empty">
                        <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 mx-auto mb-3 grid place-items-center">
                            <Broadcast size={26} weight="duotone" className="text-rose-600" />
                        </div>
                        <h3 className="font-display text-lg font-bold">
                            {items.length === 0 ? "No sessions scheduled yet" : "No sessions match this filter"}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto">
                            {items.length === 0
                                ? <>Click <strong>Schedule Session</strong> to publish your first webinar. It will show up on every doctor&apos;s Learning Academy under &ldquo;Live Sessions.&rdquo;</>
                                : "Try a different chip or clear the search box."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filtered.map((w) => <WebinarRow key={w.id} w={w} onEdit={setEditing} onDelete={remove} />)}
                    </div>
                )}
            </div>

            {editing && (
                <WebinarDialog
                    row={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await load(); }}
                />
            )}
        </div>
    );
}


function WebinarRow({ w, onEdit, onDelete }) {
    const past = isPast(w.start_at, w.duration_minutes);
    const dateLabel = w.start_at
        ? new Date(w.start_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        : "Not scheduled";

    return (
        <div className={`rounded-2xl border bg-card p-4 flex items-start gap-3 ${past ? "opacity-75" : ""}`} data-testid={`webinar-row-${w.id}`}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${w.featured ? "bg-rose-500 text-white" : "bg-secondary text-foreground"}`}>
                <VideoCamera size={18} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                    {w.featured && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500 text-white">
                            <Sparkle size={9} weight="fill" className="inline mr-0.5" /> Featured
                        </span>
                    )}
                    {past && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Past</span>
                    )}
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        {AUDIENCE_OPTIONS.find((a) => a.value === w.audience)?.label || w.audience}
                    </span>
                </div>
                <div className="font-display font-bold text-base leading-tight">{w.title}</div>
                {w.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{w.description}</p>
                )}
                <div className="mt-1.5 flex items-center gap-3 flex-wrap text-[11px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><CalendarBlank size={11} weight="fill" /> {dateLabel}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={11} weight="fill" /> {w.duration_minutes} min</span>
                    <span className="inline-flex items-center gap-1"><Users size={11} weight="fill" /> {w.host_name}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        {w.rsvp_count || 0} RSVP{w.rsvp_count === 1 ? "" : "s"}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    onClick={() => onEdit(w)}
                    className="w-8 h-8 grid place-items-center rounded-md border border-border bg-secondary hover:bg-secondary/60 transition-colors"
                    title="Edit"
                    data-testid={`webinar-edit-${w.id}`}
                >
                    <PencilSimple size={12} weight="bold" />
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(w)}
                    className="w-8 h-8 grid place-items-center rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 transition-colors"
                    title="Delete"
                    data-testid={`webinar-delete-${w.id}`}
                >
                    <Trash size={11} weight="bold" />
                </button>
            </div>
        </div>
    );
}


function WebinarDialog({ row, onClose, onSaved }) {
    const isEdit = Boolean(row);
    const [form, setForm] = useState(() => ({
        title: row?.title || "",
        description: row?.description || "",
        host_name: row?.host_name || "",
        host_avatar_url: row?.host_avatar_url || "",
        start_at: row?.start_at ? row.start_at.slice(0, 16) : "",  // datetime-local wants YYYY-MM-DDTHH:mm
        duration_minutes: row?.duration_minutes || 60,
        join_url: row?.join_url || "",
        recording_url: row?.recording_url || "",
        audience: row?.audience || "all",
        tags: (row?.tags || []).join(", "),
        featured: Boolean(row?.featured),
    }));
    const [saving, setSaving] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const save = useCallback(async () => {
        if (!form.title.trim() || !form.host_name.trim() || !form.join_url.trim() || !form.start_at) {
            toast.error("Title, host name, start time, and join URL are required.");
            return;
        }
        // Coerce datetime-local (assumed to be in the browser's local
        // timezone) into an ISO UTC string so backend + display are
        // timezone-safe.
        const isoStart = new Date(form.start_at).toISOString();
        const payload = {
            title: form.title.trim(),
            description: form.description.trim(),
            host_name: form.host_name.trim(),
            host_avatar_url: form.host_avatar_url.trim() || null,
            start_at: isoStart,
            duration_minutes: Number(form.duration_minutes) || 60,
            join_url: form.join_url.trim(),
            recording_url: form.recording_url.trim() || null,
            audience: form.audience,
            tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            featured: form.featured,
        };
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/staff/webinars/${row.id}`, payload);
                toast.success("Session updated.");
            } else {
                await api.post("/staff/webinars", payload);
                toast.success("Session scheduled.");
            }
            await onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save.");
        } finally { setSaving(false); }
    }, [form, isEdit, row, onSaved]);

    return (
        <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="webinar-dialog" onClick={onClose}>
            <div className="w-full max-w-2xl bg-card rounded-2xl shadow-2xl border border-border overflow-hidden max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-border flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Broadcast size={18} weight="duotone" className="text-rose-500" />
                        <h3 className="font-display font-bold">{isEdit ? "Edit session" : "Schedule new session"}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm font-semibold" data-testid="webinar-dialog-close">Close</button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    <Field label="Title" required>
                        <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Advanced Aligner Case Planning Workshop" data-testid="webinar-title" />
                    </Field>
                    <Field label="Description">
                        <Textarea rows={3} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="One or two paragraphs — what will doctors walk away with?" data-testid="webinar-description" />
                    </Field>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Host name" required>
                            <Input value={form.host_name} onChange={(e) => set({ host_name: e.target.value })} placeholder="Dr Suresh Nair" data-testid="webinar-host-name" />
                        </Field>
                        <Field label="Host avatar URL (optional)">
                            <Input value={form.host_avatar_url} onChange={(e) => set({ host_avatar_url: e.target.value })} placeholder="https://…" data-testid="webinar-host-avatar" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Field label="Start (local time)" required>
                            <Input
                                type="datetime-local"
                                value={form.start_at}
                                onChange={(e) => set({ start_at: e.target.value })}
                                data-testid="webinar-start"
                            />
                        </Field>
                        <Field label="Duration (min)" required>
                            <Input
                                type="number"
                                min={5}
                                max={600}
                                value={form.duration_minutes}
                                onChange={(e) => set({ duration_minutes: e.target.value })}
                                data-testid="webinar-duration"
                            />
                        </Field>
                        <Field label="Audience">
                            <select
                                value={form.audience}
                                onChange={(e) => set({ audience: e.target.value })}
                                className="h-10 px-3 rounded-md border border-input bg-background text-sm w-full"
                                data-testid="webinar-audience"
                            >
                                {AUDIENCE_OPTIONS.map((a) => (
                                    <option key={a.value} value={a.value}>{a.label}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                    <Field label="Join URL (Zoom / Meet / Teams)" required>
                        <div className="relative">
                            <LinkSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold" />
                            <Input
                                value={form.join_url}
                                onChange={(e) => set({ join_url: e.target.value })}
                                placeholder="https://zoom.us/j/…"
                                className="pl-9"
                                data-testid="webinar-join-url"
                            />
                        </div>
                    </Field>
                    <Field label="Recording URL (after the session)">
                        <div className="relative">
                            <LinkSimple size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" weight="bold" />
                            <Input
                                value={form.recording_url}
                                onChange={(e) => set({ recording_url: e.target.value })}
                                placeholder="https://…"
                                className="pl-9"
                                data-testid="webinar-recording-url"
                            />
                        </div>
                    </Field>
                    <Field label="Tags (comma-separated)">
                        <Input value={form.tags} onChange={(e) => set({ tags: e.target.value })} placeholder="aligners, clinical, biomechanics" data-testid="webinar-tags" />
                    </Field>
                    <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-rose-200 bg-rose-50/50 hover:bg-rose-50">
                        <input
                            type="checkbox"
                            checked={form.featured}
                            onChange={(e) => set({ featured: e.target.checked })}
                            data-testid="webinar-featured"
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-sm font-semibold flex items-center gap-1"><Sparkle size={12} weight="fill" className="text-rose-500" /> Feature on doctor Academy</div>
                            <div className="text-[11px] text-muted-foreground">Featured sessions pin to the top of the Live Sessions tab with a rose accent.</div>
                        </div>
                    </label>
                </div>

                <div className="px-5 py-3 border-t border-border bg-secondary/20 flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose} data-testid="webinar-dialog-cancel">Cancel</Button>
                    <Button size="sm" onClick={save} disabled={saving} data-testid="webinar-dialog-save">
                        {saving ? "Saving…" : (isEdit ? "Update session" : "Schedule session")}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
